import { Grid, GridProps } from '@mui/material';
import { useState, createContext, Dispatch, SetStateAction, useMemo, useEffect } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { some } from '../constants';
import GroupFields from './GroupFields';
import SchemaElement from './SchemaElement';
import useMappedFieldForm from './useMappedFieldForm';
import {
  FieldValues,
  FieldsElementType,
  FormProps,
  ISchemaForm,
  IUIKey,
  mergeFieldName,
} from './utils';

// eslint-disable-next-line react-refresh/only-export-components
export const StringerEvent = createContext<{
  stringer: boolean;
  setStringer: Dispatch<SetStateAction<boolean>>;
}>({ stringer: false, setStringer: () => {} });

interface Props<
  T extends FieldValues = FieldValues,
  K extends IUIKey = 'default',
  F extends FormProps = FormProps,
  R extends FieldValues = T,
> {
  schema: ISchemaForm<T, K, F, R>;
  fieldName?: FieldPath<R>;
  showSubmitButton?: boolean;
  formProps: F;
  propsGridContainer?: GridProps;
}

const SchemaView = <
  T extends FieldValues = FieldValues,
  K extends IUIKey = string,
  F extends FormProps = any,
  R extends FieldValues = T,
>(
  props: Props<T, K, F, R>
) => {
  const { schema, fieldName, showSubmitButton = false, formProps } = props;
  const { onChange } = formProps;
  const { propsGridContainer } = schema;
  const methods = useFormContext<R>();
  const { watch } = methods;
  const valueField = fieldName ? watch(fieldName) : watch();
  const [stringer, setStringer] = useState(false);
  const { groupFields } = useMappedFieldForm<T, K, F, R>({
    methods,
    formProps,
    schema,
    fieldName,
    showSubmitButton,
  });

  const listElement = useMemo(() => {
    const tmp: any[] = [];
    groupFields?.forEach((schemaFiled: FieldsElementType, index: number) => {
      if (!schemaFiled.hidden) {
        tmp.push(
          <GroupFields<R>
            key={index}
            schema={{
              // propsGridContainer của schema sẽ bị ghi đề bởi propsGridContainer trong UI
              ...schemaFiled,
              propsGridContainer: {
                ...propsGridContainer,
                ...schemaFiled?.propsGridContainer,
              },
            }}
            fieldName={fieldName}
          />
        );
      }
    });
    return tmp;
  }, [fieldName, groupFields, propsGridContainer]);

  const listElementNoHidden = useMemo(() => {
    const tmp: any[] = [];
    groupFields?.forEach((schemaFiled: FieldsElementType, index: number) => {
      tmp.push(
        <GroupFields<R>
          key={index}
          schema={{
            ...schemaFiled,
            propsGridContainer: {
              ...propsGridContainer,
              ...schemaFiled?.propsGridContainer,
            },
          }}
          fieldName={fieldName}
        />
      );
    });
    return tmp;
  }, [fieldName, groupFields, propsGridContainer]);

  const getFieldsElement = useMemo(() => {
    return groupFields?.reduce((value: any, current: FieldsElementType, index: number) => {
      return {
        ...value,
        [current?.id || index]: current?.fields.reduce(
          (val: some, { key_element, ...cur }: some, idx: number) => {
            return {
              ...val,
              [key_element]: (
                <SchemaElement<T>
                  key={`${index}-${idx}`}
                  propsElement={cur}
                  fieldName={mergeFieldName({
                    name: key_element,
                    parent: fieldName,
                  })}
                  rawElement
                />
              ),
            };
          },
          {}
        ),
      };
    }, {}) as any;
  }, [fieldName, groupFields]);

  useEffect(() => {
    if (onChange) onChange(valueField, methods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringer]);

  if (!schema) {
    return null;
  }

  const view = (
    <Grid
      container
      columnSpacing={{ mobile: 0.5, tablet: 1, laptop: 2 }}
      rowSpacing={{ mobile: 1, tablet: 1, laptop: 2 }}
      wrap="wrap"
      {...props?.propsGridContainer}
    >
      {listElement}
    </Grid>
  );

  return (
    <StringerEvent.Provider value={{ stringer, setStringer }}>
      {schema?.layout
        ? schema.layout({
            valuesField: valueField,
            fields: getFieldsElement,
            methods,
            formProps,
            view,
            listElement: listElementNoHidden,
          })
        : view}
    </StringerEvent.Provider>
  );
};

export default SchemaView;
